"use client";

import { keepPreviousData } from "@tanstack/react-query";
import { useGetSearchResults } from "@/lib/api/generated";
import { searchResultsQueryKey } from "@/hooks/use-project-search-runs-stream";
import {
  isRunning,
  selectRun,
  useSearchRunsStore,
} from "@/stores/search-runs-store";

/**
 * One page of ranked results for a saved search.
 *
 * Gated on the runs store: the request only fires once we know which READY
 * run produced the rows and no run is in flight, so a page never renders
 * results from a superseded run. A failed or cancelled re-run still serves
 * the earlier ready run's rows (the backend pages the latest READY run). The
 * run id is part of the key so a re-run gets a fresh cache entry, and the
 * key sits under `searchResultsQueryKey(searchId)` so the SSE hook's prefix
 * invalidation on `run.ready` / `run.failed` refreshes every loaded page.
 *
 * `keepPreviousData` keeps the last page on screen while the next one loads.
 * Because the key changes across searches and runs, that placeholder can be
 * another run's page: callers must check `data.run_id` against the run they
 * are rendering before painting rows (the results provider does).
 */
export function useSearchResults(
  searchId: string | null,
  page: number,
  perPage: number,
) {
  const run = useSearchRunsStore(selectRun(searchId));
  const readyRunId = run?.latestReadyRunId ?? null;
  const enabled =
    !!searchId && !!run && !isRunning(run.phase) && !!readyRunId;

  return useGetSearchResults(
    searchId ?? "",
    { page, per_page: perPage },
    {
      query: {
        queryKey: [
          ...searchResultsQueryKey(searchId ?? ""),
          readyRunId,
          page,
          perPage,
        ],
        enabled,
        placeholderData: keepPreviousData,
        staleTime: 5 * 60_000,
      },
    },
  );
}
